import { supabase } from "@/integrations/supabase/client";

// The suburb_profile_* tables aren't in the auto-generated Supabase types
// yet (the user re-generates types in a separate workflow). Cast through
// `untypedSupabase` for these queries — they fall back to runtime
// behaviour with explicit Promise types.
type SupabaseClient = typeof supabase;
type UntypedFrom = { from: (table: string) => ReturnType<SupabaseClient["from"]> };
const untypedSupabase = supabase as unknown as SupabaseClient & UntypedFrom;

export interface SuburbProfileAdapter {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  base_url: string;
  status: string;
  default_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SuburbProfileRun {
  id: string;
  suburb_profile_adapter_id: string;
  run_type: "scheduled" | "manual";
  status: "running" | "completed" | "failed";
  started_at: string;
  completed_at: string | null;
  suburbs_total: number;
  suburbs_done: number;
  suburbs_failed: number;
  failed_suburbs: Array<{ suburbId: string; url: string; reason: string }>;
  last_error: string | null;
  metadata: Record<string, unknown>;
}

export interface SuburbProfileSnapshot {
  id: string;
  suburb_id: string | null;
  suburb_profile_adapter_id: string;
  suburb_profile_run_id: string | null;
  source_url: string;
  snapshot_month: string;
  median_house_price: number | null;
  median_unit_price: number | null;
  median_house_rent_weekly: number | null;
  median_unit_rent_weekly: number | null;
  house_sales_volume_12mo: number | null;
  unit_sales_volume_12mo: number | null;
  annual_growth_pct: number | null;
  five_year_growth_pct: number | null;
  rental_yield_house_pct: number | null;
  rental_yield_unit_pct: number | null;
  days_on_market_house: number | null;
  days_on_market_unit: number | null;
  population: number | null;
  median_age: number | null;
  median_household_income: number | null;
  /**
   * Per-bedroom-band breakdown. Array of objects each describing a
   * bedroom band's median sold price, sales volume, days on market,
   * growth history etc. Mirrors `BedroomBand` in @repo/suburb-adapters.
   */
  bedroom_breakdowns: BedroomBandRow[] | null;
  /** Demographic stats per the suburb-adapters Demographics type. */
  demographics: DemographicsRow | null;
  raw_data: Record<string, unknown>;
  page_snapshot_id: string | null;
  created_at: string;
}

export interface BedroomBandRow {
  bedrooms?: number | null;
  dwellingType?: string | null;
  medianSoldPrice?: number | null;
  medianRentWeekly?: number | null;
  daysOnMarket?: number | null;
  salesVolume12mo?: number | null;
  annualGrowthPct?: number | null;
  growthHistory?: Array<{
    year: number;
    annualGrowth?: number | null;
    numberSold?: number | null;
  }>;
}

export interface DemographicsRow {
  medianAge?: number | null;
  medianHouseholdIncome?: number | null;
  population?: number | null;
  renterPct?: number | null;
  marriedPct?: number | null;
  singlePct?: number | null;
  ownerOccupierPct?: number | null;
  mostCommonAgeBracket?: string | null;
}

export interface LatestSuburbSnapshot extends SuburbProfileSnapshot {
  suburb_name: string;
  state: string;
  postcode: string;
  adapter_slug: string | null;
  adapter_name: string | null;
}

export async function fetchSuburbProfileAdapters(): Promise<SuburbProfileAdapter[]> {
  const { data, error } = await untypedSupabase
    .from("suburb_profile_adapters")
    .select("*")
    .order("name");
  if (error) throw error;
  return (data ?? []) as unknown as SuburbProfileAdapter[];
}

export async function fetchSuburbProfileAdapter(slug: string): Promise<SuburbProfileAdapter | null> {
  const { data, error } = await untypedSupabase
    .from("suburb_profile_adapters")
    .select("*")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as SuburbProfileAdapter) ?? null;
}

export async function fetchSuburbProfileRuns(
  adapterId: string,
  limit = 12,
): Promise<SuburbProfileRun[]> {
  const { data, error } = await untypedSupabase
    .from("suburb_profile_runs")
    .select("*")
    .eq("suburb_profile_adapter_id", adapterId)
    .order("started_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as SuburbProfileRun[];
}

export async function fetchSuburbProfileSnapshots(
  suburbId: string,
  limit = 36,
): Promise<SuburbProfileSnapshot[]> {
  const { data, error } = await untypedSupabase
    .from("suburb_profile_snapshots")
    .select("*")
    .eq("suburb_id", suburbId)
    .order("snapshot_month", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data ?? []) as unknown as SuburbProfileSnapshot[];
}

/**
 * Latest snapshot per suburb that has at least one — for the
 * Suburb Information list page. Joins `suburbs` so the table can show
 * the human-readable name without an extra round-trip.
 */
export async function fetchLatestSuburbSnapshots(): Promise<LatestSuburbSnapshot[]> {
  // One row per (suburb, adapter, month) ordered newest first. The list
  // page reduces to the latest per (suburb × adapter) so users can see
  // both the Domain row and the realestate.com.au row for the same
  // suburb side-by-side without losing either.
  const { data, error } = await untypedSupabase
    .from("suburb_profile_snapshots")
    .select(
      "*, suburbs!inner(suburb_name, state, postcode), suburb_profile_adapters!inner(slug, name)",
    )
    .order("snapshot_month", { ascending: false });
  if (error) throw error;
  const rows = (data ?? []) as unknown as Array<
    SuburbProfileSnapshot & {
      suburbs: { suburb_name: string; state: string; postcode: string };
      suburb_profile_adapters: { slug: string; name: string };
    }
  >;
  const seen = new Set<string>();
  const latest: LatestSuburbSnapshot[] = [];
  for (const row of rows) {
    if (!row.suburb_id) continue;
    const key = `${row.suburb_id}|${row.suburb_profile_adapter_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    latest.push({
      ...row,
      suburb_name: row.suburbs.suburb_name,
      state: row.suburbs.state,
      postcode: row.suburbs.postcode,
      adapter_slug: row.suburb_profile_adapters?.slug ?? null,
      adapter_name: row.suburb_profile_adapters?.name ?? null,
    });
  }
  return latest;
}

/**
 * Pull every snapshot for a suburb across all adapters, with adapter
 * info eagerly joined. Used by SuburbInformationDetail to render an
 * adapter-aware history table + sparklines.
 */
export interface SuburbProfileSnapshotWithAdapter extends SuburbProfileSnapshot {
  adapter_slug: string | null;
  adapter_name: string | null;
}

export async function fetchSuburbProfileSnapshotsWithAdapter(
  suburbId: string,
  limit = 60,
): Promise<SuburbProfileSnapshotWithAdapter[]> {
  const { data, error } = await untypedSupabase
    .from("suburb_profile_snapshots")
    .select("*, suburb_profile_adapters!inner(slug, name)")
    .eq("suburb_id", suburbId)
    .order("snapshot_month", { ascending: false })
    .limit(limit);
  if (error) throw error;
  const rows = (data ?? []) as unknown as Array<
    SuburbProfileSnapshot & { suburb_profile_adapters: { slug: string; name: string } }
  >;
  return rows.map((row) => ({
    ...row,
    adapter_slug: row.suburb_profile_adapters?.slug ?? null,
    adapter_name: row.suburb_profile_adapters?.name ?? null,
  }));
}

export interface TriggerSuburbCollectionInput {
  suburbIds?: string[];
  runType?: "scheduled" | "manual";
  force?: boolean;
  adapterSlugs?: string[];
}

export interface TriggerSuburbCollectionResult {
  runId: string | null;
  status: "running" | "completed" | "failed";
  suburbsTotal: number;
  done: number;
  failed: number;
  failedSuburbs: Array<{ suburbId: string; url: string; reason: string }>;
  runs?: Array<{
    runId: string;
    adapterSlug: string;
    status: "running" | "completed" | "failed";
    suburbsTotal: number;
    done: number;
    failed: number;
    failedSuburbs: Array<{ suburbId: string; url: string; reason: string }>;
  }>;
}

export async function triggerSuburbCollection(
  input: TriggerSuburbCollectionInput = {},
): Promise<TriggerSuburbCollectionResult> {
  const { data, error } = await supabase.functions.invoke<TriggerSuburbCollectionResult>(
    "collect-suburb-stats",
    {
      body: {
        runType: input.runType ?? "manual",
        suburbIds: input.suburbIds,
        force: input.force ?? false,
        adapterSlugs: input.adapterSlugs,
      },
    },
  );
  if (error) throw error;
  if (!data) throw new Error("collect-suburb-stats returned no body");
  return data;
}
